/**
 * Site QA primary workspace: approved checklist + Generate / Run tasks.
 * Fail inbox shows screenshot paths only.
 */

import { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  PiArrowClockwiseBold,
  PiCheckBold,
  PiListChecksBold,
  PiPlayBold,
  PiStopBold,
  PiUploadSimpleBold,
} from "react-icons/pi";

import { generateRunTasks } from "./generate";
import { draftChecklistFromBrain } from "./draft";
import { approvedGrokItems, checklistComplete } from "./protocol";
import { runQaLoop } from "./qaLoop";
import { hardResetSession, probeSidecar, type SidecarHealth } from "./sidecar";
import {
  exportChecklist,
  loadSession,
  newItemId,
  parseGrokChecklist,
  saveSession,
} from "./store";
import type { QaItem, QaSession } from "./types";
import type { InferenceTarget } from "../suite/inference";

type Props = {
  brainTarget: InferenceTarget | null;
  onStatus: (msg: string) => void;
};

export const SiteQaPanel = memo(({ brainTarget, onStatus }: Props) => {
  const [session, setSession] = useState<QaSession>(() => loadSession());
  const [importText, setImportText] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [failPaths, setFailPaths] = useState<string[]>([]);
  const [health, setHealth] = useState<SidecarHealth | null>(null);
  const [healthErr, setHealthErr] = useState<string | null>(null);
  const [draftBusy, setDraftBusy] = useState(false);
  const [line, setLine] = useState("Approve Grok boxes, Generate, then Run tasks");
  const note = (msg: string) => {
    setLine(msg);
    onStatus(msg);
  };

  const stopRef = useRef(false);

  const persist = useCallback((next: QaSession) => {
    setSession(next);
    saveSession(next);
  }, []);

  const refreshHealth = useCallback(async () => {
    try {
      const h = await probeSidecar();
      setHealth(h);
      setHealthErr(null);
    } catch (e) {
      setHealth(null);
      setHealthErr(e instanceof Error ? e.message : "sidecar offline");
    }
  }, []);

  useEffect(() => {
    void refreshHealth();
  }, [refreshHealth]);

  const boxes = approvedGrokItems(session.items);
  const passed = boxes.filter((i) => i.status === "pass").length;
  const complete = checklistComplete(session.items);

  const generate = () => {
    const next = generateRunTasks(session);
    persist(next);
    note(
      next.tasks.length
        ? `Generated ${next.tasks.length} run task${next.tasks.length === 1 ? "" : "s"}`
        : "Approve Grok boxes first",
    );
  };

  const runTasks = async () => {
    if (session.running) return;
    stopRef.current = false;
    persist({ ...session, running: true });
    const result = await runQaLoop(session, {
      shouldStop: () => stopRef.current,
      onStatus: note,
      onSession: persist,
      onFailPath: (path) => setFailPaths((prev) => [path, ...prev.filter((p) => p !== path)]),
    });
    persist({ ...result, running: false });
  };

  const stop = () => {
    stopRef.current = true;
    persist({ ...session, running: false });
    note("Stop requested");
  };

  const reset = async () => {
    if (!session.sidecarSessionId) {
      note("No live browser session — Run tasks first");
      return;
    }
    try {
      await hardResetSession(session.sidecarSessionId, session.targetUrl);
      note("Hard reset");
    } catch (e) {
      note(e instanceof Error ? e.message : "Hard reset failed");
    }
  };

  const setApproved = (id: string, approved: boolean) => {
    persist({
      ...session,
      items: session.items.map((i) => (i.id === id ? { ...i, approved, status: "pending" } : i)),
    });
  };

  const importGrok = () => {
    try {
      const parsed = parseGrokChecklist(importText);
      persist({
        ...session,
        items: parsed.items,
        targetUrl: parsed.targetUrl || session.targetUrl,
        repoPath: parsed.repoPath || session.repoPath,
        tasks: [],
        generatedAt: undefined,
      });
      setImportText("");
      setShowImport(false);
      note(`Imported ${parsed.items.length} Grok boxes — approve to generate`);
    } catch (e) {
      note(e instanceof Error ? e.message : "Invalid checklist JSON");
    }
  };

  const draft = async () => {
    if (!brainTarget) {
      note("Connect a catalog brain to draft boxes");
      return;
    }
    setDraftBusy(true);
    try {
      const items = await draftChecklistFromBrain(brainTarget, session.targetUrl);
      persist({ ...session, items, tasks: [], generatedAt: undefined });
      note(`Drafted ${items.length} boxes — approve each to generate`);
    } catch (e) {
      note(e instanceof Error ? e.message : "Draft failed");
    } finally {
      setDraftBusy(false);
    }
  };

  const addOwnerBox = () => {
    const item: QaItem = {
      id: newItemId(),
      title: "",
      actions: [],
      authoredBy: "owner",
      approved: false,
      status: "pending",
      retries: 0,
    };
    persist({ ...session, items: [...session.items, item] });
  };

  const setTitle = (id: string, title: string) => {
    persist({
      ...session,
      items: session.items.map((i) => (i.id === id ? { ...i, title } : i)),
    });
  };

  return (
    <div className="af-qa">
      <div className="af-qa-toolbar">
        <label className="af-qa-field">
          Site
          <input
            className="af-endpoint-input"
            value={session.targetUrl}
            disabled={session.running}
            onChange={(e) => persist({ ...session, targetUrl: e.target.value })}
            placeholder="https://…"
          />
        </label>
        <label className="af-qa-field">
          Fixer repo
          <input
            className="af-endpoint-input"
            value={session.repoPath}
            disabled={session.running}
            onChange={(e) => persist({ ...session, repoPath: e.target.value })}
            placeholder="C:\Users\coate\Projects\…"
          />
        </label>
        <button type="button" className="af-chip" onClick={() => void refreshHealth()}>
          Sidecar{" "}
          {health?.ok ? "ok" : healthErr ? "off" : "…"}
        </button>
      </div>

      <div className="af-run-controls">
        <button
          type="button"
          className="af-btn"
          disabled={session.running}
          onClick={generate}
        >
          <PiListChecksBold /> Generate
        </button>
        {!session.running ? (
          <button type="button" className="af-btn af-btn--primary" onClick={() => void runTasks()}>
            <PiPlayBold /> Run tasks
          </button>
        ) : (
          <button type="button" className="af-btn af-btn--danger" onClick={stop}>
            <PiStopBold /> Stop
          </button>
        )}
        <button type="button" className="af-btn" onClick={() => void reset()} disabled={session.running}>
          <PiArrowClockwiseBold /> Hard reset
        </button>
        <span className="af-qa-progress">
          {boxes.length ? (
            <>
              {passed}/{boxes.length}
              {complete ? " · complete" : ""}
            </>
          ) : (
            "Approve Grok boxes"
          )}
        </span>
      </div>
      <div className="af-status-line">
        <span>{line}</span>
      </div>

      <div className="af-qa-actions-row">
        <button
          type="button"
          className="af-chip"
          disabled={session.running || draftBusy}
          onClick={() => void draft()}
        >
          Draft from brain
        </button>
        <button
          type="button"
          className="af-chip"
          disabled={session.running}
          onClick={() => setShowImport((v) => !v)}
        >
          <PiUploadSimpleBold /> Import Grok JSON
        </button>
        <button
          type="button"
          className="af-chip"
          disabled={session.running}
          onClick={addOwnerBox}
        >
          Add box
        </button>
        <button
          type="button"
          className="af-chip"
          onClick={() => {
            const blob = new Blob([exportChecklist(session)], { type: "application/json" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = "agentforge-qa-checklist.json";
            a.click();
            URL.revokeObjectURL(a.href);
          }}
        >
          Export
        </button>
      </div>

      {showImport && (
        <div className="af-qa-import">
          <textarea
            className="af-goal"
            rows={5}
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder='[{"id":"login","title":"Google login visible","actions":[{"type":"expectText","text":"Google"}]}]'
            disabled={session.running}
          />
          <button type="button" className="af-btn af-btn--primary" onClick={importGrok}>
            Load boxes
          </button>
        </div>
      )}

      <ol className="af-qa-list">
        {session.items.length === 0 && (
          <li className="af-muted">Import or draft Grok boxes, then approve each.</li>
        )}
        {session.items.map((item) => (
          <li key={item.id} className={`af-qa-item af-qa-item--${item.status}`}>
            <label className="af-check af-qa-approve">
              <input
                type="checkbox"
                checked={item.approved}
                disabled={session.running || item.authoredBy !== "grok"}
                onChange={(e) => setApproved(item.id, e.target.checked)}
              />
              Approve
            </label>
            <input
              className="af-qa-title"
              value={item.title}
              disabled={session.running}
              onChange={(e) => setTitle(item.id, e.target.value)}
            />
            <span className="af-qa-meta">
              {item.authoredBy}
              {item.status === "pass" ? (
                <PiCheckBold className="af-qa-pass-icon" />
              ) : (
                ` · ${item.status}`
              )}
            </span>
          </li>
        ))}
      </ol>

      {failPaths.length > 0 && (
        <aside className="af-qa-inbox" aria-label="Fail screenshot paths">
          <h3>Fail paths</h3>
          <ul className="af-qa-paths">
            {failPaths.map((p) => (
              <li key={p} className="af-mono">
                {p}
              </li>
            ))}
          </ul>
        </aside>
      )}
    </div>
  );
});

SiteQaPanel.displayName = "SiteQaPanel";
